import React, { useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { removeFromCart } from '../redux/slice/user.slice';
import { fetchActiveDiscounts } from '../redux/slice/discount.slice';
import { getGamePriceDetails } from '../utils/gamePricing';
import { fetchPublicGatewaySettings } from '../redux/slice/gateway.slice';
import { Trash2, ShoppingBag, ArrowRight, ArrowLeft, Shield, Award, Tag, ChevronRight } from 'lucide-react';
import gsap from 'gsap';
import LazyImage from '../components/common/LazyImage';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import cartAstronautImg from '../assets/images/cart.webp';

const PlatformIconMap = {
    windows: FaWindows,
    playstation: SiPlaystation,
    xbox: FaXbox,
    nintendo: BsNintendoSwitch,
    quest: SiOculus,
    android: DiAndroid,
    ios: FaApple,
    visionpro: TbDeviceVisionProFilled,
    steamdeck: SiSteamdeck
};

const PlatformColorMap = {
    windows: '#0078d7',
    playstation: '#3b82f6',
    xbox: '#107c10',
    nintendo: '#e60012',
    quest: '#ffffff',
    android: '#3ddc84',
    ios: '#a3aaae',
    visionpro: '#a855f7',
    steamdeck: '#60a5fa'
};

export default function Cart() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { cart } = useSelector((s) => s.userSlice);
    const { discounts } = useSelector((s) => s.discount);
    const { publicSettings } = useSelector((s) => s.gateway);

    const isPurchasingRestricted = () => {
        if (!publicSettings) return false;
        if (publicSettings.purchasePaused) {
            if (publicSettings.purchaseStartsAt) {
                const startsAt = new Date(publicSettings.purchaseStartsAt).getTime();
                return startsAt > Date.now();
            }
            return true;
        }
        return false;
    };

    const restricted = isPurchasingRestricted();

    useEffect(() => {
        dispatch(fetchActiveDiscounts());
        if (!publicSettings) {
            dispatch(fetchPublicGatewaySettings());
        }
    }, [dispatch, publicSettings]);

    const handleBack = () => {
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate('/all-games');
        }
    };

    const cartTotal = cart.reduce((acc, item) => {
        const details = getGamePriceDetails(item.game, item.platform, discounts);
        return acc + details.price;
    }, 0);
    const taxRatePercent = publicSettings?.taxRate ?? 10;
    const tax = Number((cartTotal * (taxRatePercent / 100)).toFixed(2));
    const total = cartTotal + tax;

    const headerRef = useRef(null);

    useEffect(() => {
        if (headerRef.current) {
            gsap.fromTo(headerRef.current,
                { opacity: 0, y: 30 },
                { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
            );
        }
    }, []);

    return (
        <div className="min-h-screen pt-24 pb-12 sm:pt-32 sm:pb-32 relative">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-cyber-blue/4 rounded-full blur-[140px] pointer-events-none"></div>

            <div className="container mx-auto">
                <div ref={headerRef} className="mb-6 sm:mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 relative z-10">
                    <div className="flex-shrink-0">
                        <button
                            onClick={handleBack}
                            className="inline-flex items-center justify-center gap-2 mb-4 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-[#151a26] border border-white/[0.08] text-xs font-cyber font-bold text-gray-200 hover:text-white hover:border-white/20 hover:bg-[#1a2130] transition-all duration-300 uppercase tracking-wider group/btn shadow-sm cursor-pointer"
                        >
                            <ArrowLeft className="w-3.5 h-3.5 group-hover/btn:-translate-x-1 transition-transform" />
                            <span>BACK</span>
                        </button>
                        <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                            Shopping Cart
                        </p>
                        <h1 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-nowrap">
                            YOUR <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">CART</span>
                        </h1>
                        <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full"></div>
                    </div>
                </div>

                <style>{`
                    @keyframes astronautFloat {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-14px) rotate(1.8deg); }
                    }
                    .animate-astronaut-float {
                        animation: astronautFloat 3.8s ease-in-out infinite;
                    }
                `}</style>

                {cart.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center max-w-lg mx-auto space-y-4 px-4">
                        <div className="relative group w-full flex items-center justify-center p-1 sm:p-2">
                            {/* Dark Teal Radial Glow Backdrop */}
                            <div
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 xs:w-68 sm:w-80 h-60 xs:h-68 sm:h-80 rounded-full blur-2xl pointer-events-none opacity-90"
                            />
                            <img
                                src={cartAstronautImg}
                                alt="Cart Empty Astronaut"
                                className="w-36 xs:w-44 sm:w-52 md:w-56 max-w-[240px] max-h-[250px] h-auto object-contain relative z-10 animate-astronaut-float cursor-pointer transition-transform duration-500 group-hover:scale-105"
                            />
                        </div>

                        <div className="space-y-2.5 max-w-md">
                            <h2 className="font-cyber font-black text-2xl md600:text-3xl text-[#00f0ff] uppercase tracking-wider drop-shadow-[0_0_18px_rgba(0,240,255,0.4)]">
                                YOUR CART IS EMPTY
                            </h2>
                            <p className="text-xs md600:text-sm text-gray-300 font-sans leading-relaxed">
                                Looks like your cart is empty! Add some games to your library and experience next-gen gaming.
                            </p>
                        </div>

                        <button
                            onClick={() => navigate('/all-games')}
                            className="w-full sm:w-auto relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-2.5 sm:px-7 sm:py-3 rounded-xl bg-gradient-to-r from-[#00f0ff] via-[#5c60ff] to-[#bd00ff] text-black font-cyber font-bold text-xs sm:text-[13px] uppercase tracking-wider hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap group before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
                        >
                            <span>DISCOVER GAMES</span>
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Cart Items */}
                        <div className="lg:col-span-2 space-y-4">
                            {cart.map(({ game, quantity, platform }) => {
                                const details = getGamePriceDetails(game, platform, discounts);
                                const selectedPlat = (platform || 'windows').toLowerCase();
                                const PlatformIcon = PlatformIconMap[selectedPlat] || FaWindows;
                                return (
                                    <div key={`${game._id || game.id}-${platform || 'windows'}`} className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] p-3 md600:p-4 flex gap-3 md600:gap-4 group hover:border-cyber-cyan/30 transition-all">
                                        <LazyImage
                                            src={game.cover_image?.url || game.cover_image || game.image?.url || game.image}
                                            placeholder={game.cover_image?.lowUrl || game.image?.lowUrl}
                                            alt={game.title}
                                            className="w-full h-full object-cover"
                                            containerClassName="w-20 h-20 md600:w-24 md600:h-24 rounded-xl overflow-hidden flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0 pr-10 flex flex-col justify-center gap-1">
                                            {/* Platform Icon & Category Name */}
                                            <div className="flex items-center gap-2 text-gray-400 text-xs md600:text-md600 font-sans font-medium">
                                                {PlatformIcon && (
                                                    <PlatformIcon
                                                        className="w-4 h-4 md600:w-4.5 md600:h-4.5 flex-shrink-0"
                                                        style={{ color: PlatformColorMap[selectedPlat] || '#3b82f6' }}
                                                    />
                                                )}
                                                <span className="truncate capitalize">{game.category?.name || 'Game'}</span>
                                            </div>

                                            {/* Title */}
                                            <h3 className="font-cyber font-bold text-xs md600:text-base text-white tracking-tight">
                                                {game.title}
                                            </h3>

                                            {/* Price */}
                                            <div className="flex items-baseline gap-2 pt-0.5">
                                                <span className="text-cyber-cyan font-cyber font-extrabold text-base md600:text-lg">
                                                    ${details.price.toFixed(2)}
                                                </span>
                                                {details.discount > 0 && (
                                                    <>
                                                        <span className="text-xs text-gray-500 line-through font-mono">
                                                            ${details.originalPrice.toFixed(2)}
                                                        </span>
                                                        <span className="text-cyber-green text-[9px] font-cyber font-bold">-{details.discount}% OFF</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <button onClick={() => dispatch(removeFromCart({ gameId: game._id || game.id, platform: platform || 'windows' }))}
                                            className="absolute top-3 right-3 p-1.5 md600:p-2 rounded-lg bg-white/5 hover:bg-cyber-pink/20 border border-white/10 hover:border-cyber-pink/40 text-gray-500 hover:text-cyber-pink transition-all">
                                            <Trash2 className="w-3.5 h-3.5 md600:w-4 md600:h-4" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Order Summary */}
                        <div className="relative rounded-2xl overflow-hidden h-fit"
                            style={{ background: 'linear-gradient(135deg, #0c0c18 0%, #080810 100%)', boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 20px 60px rgba(0,0,0,0.5)' }}>
                            <div className="absolute top-0 left-0 w-full h-[1.5px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple opacity-70"></div>
                            <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
                                style={{ backgroundImage: 'radial-gradient(circle, rgba(0,240,255,0.8) 1px, transparent 1px)', backgroundSize: '28px 28px' }}></div>

                            <div className="relative z-10 p-6 space-y-5">
                                <p className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">ORDER SUMMARY</p>

                                <div className="space-y-3 py-4 border-y border-white/5">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500 font-cyber">SUBTOTAL</span>
                                        <span className="text-white font-cyber font-medium">${cartTotal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500 font-cyber">TAX ({taxRatePercent}%)</span>
                                        <span className="text-white font-cyber font-medium">${tax.toFixed(2)}</span>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between py-1">
                                    <span className="text-xs font-cyber font-medium text-gray-400 uppercase tracking-widest">TOTAL</span>
                                    <span className="text-2xl font-cyber font-semibold text-white">${total.toFixed(2)}</span>
                                </div>

                                <button
                                    onClick={() => navigate('/checkout')}
                                    disabled={cart.length === 0 || restricted}
                                    className="cyber-btn w-full !py-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    {restricted ? 'PURCHASING RESTRICTED' : 'PROCEED TO CHECKOUT'} <ArrowRight className="w-4 h-4" />
                                </button>

                                <div className="space-y-2 pt-4 border-t border-white/5">
                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                        <Shield className="w-3.5 h-3.5 text-cyber-blue" />
                                        <span className="font-mono">Secure transactions</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                        <Award className="w-3.5 h-3.5 text-cyber-blue" />
                                        <span className="font-mono">Instant library delivery</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                        <Tag className="w-3.5 h-3.5 text-cyber-blue" />
                                        <span className="font-mono">Lifetime launcher access</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
